import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import { useEffect, useState, useRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { searchService, SearchResult } from '@/services/search.service';
import { rolesService, Role } from '@/services/roles.service';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@/components/common/BrandLogo';
import { isAdminUser } from '@/utils/role';
import {
  HomeIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  TicketIcon,
  Squares2X2Icon,
  BanknotesIcon,
  ChartBarIcon,
  PlusCircleIcon,
  ListBulletIcon,
  StarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ShoppingBagIcon,
  ArrowTopRightOnSquareIcon,
  CubeIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  RectangleGroupIcon,
  ShieldCheckIcon,
  PresentationChartLineIcon,
  UserCircleIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';

export const DashboardSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isOpen, closeSidebar } = useSidebar();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [userRole, setUserRole] = useState<Role | null>(null);

  // Charger le rôle seulement si roleName absent (sessions anciennes / cache)
  useEffect(() => {
    const loadUserRole = async () => {
      if (user?.roleName) {
        setUserRole(null);
        return;
      }
      if (user?.role_id) {
        try {
          const role = await rolesService.getById(user.role_id);
          setUserRole(role);
        } catch (error) {
          console.error('Erreur lors du chargement du rôle:', error);
          setUserRole(null);
        }
      } else {
        setUserRole(null);
      }
    };
    loadUserRole();
  }, [user?.role_id, user?.roleName]);

  // Admin via roleName (source de vérité) ; fallback API rôle si besoin
  const isAdmin = isAdminUser(user, userRole);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleNavClick = (_path?: string, _e?: React.MouseEvent<HTMLAnchorElement>) => {
    // Détecter si on est sur mobile
    const isMobile = window.innerWidth < 1024;
    
    if (isMobile) {
      // Sur mobile, on veut fermer le sidebar après navigation
      // On laisse le Link gérer la navigation normalement (pas de preventDefault)
      // Puis on ferme le sidebar avec un petit délai pour s'assurer que la navigation est déclenchée
      setTimeout(() => {
        closeSidebar();
      }, 100);
    }
    // Sur desktop, on laisse le Link gérer la navigation normalement (pas de fermeture)
  };

  const handleLogout = async () => {
    await logout();
  };

  // Recherche avec debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchService.searchAll(searchQuery);
        setSearchResults(results);
        setShowResults(results.length > 0);
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Fermer les résultats quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('🔍 Recherche - Valeur changée:', value);
    setSearchQuery(value);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('🔍 Recherche - Soumission du formulaire, query:', searchQuery);
    
    if (searchQuery.trim().length >= 2) {
      if (searchResults.length > 0) {
        handleResultClick(searchResults[0]);
      } else {
        console.log('🔍 Aucun résultat disponible pour la recherche');
        // Optionnel : on peut forcer une recherche même sans résultats
      }
    } else {
      console.log('🔍 Requête trop courte (minimum 2 caractères)');
    }
  };

  const handleResultClick = (result: SearchResult) => {
    console.log('🔍 Navigation vers:', result.url);
    navigate(result.url);
    setSearchQuery('');
    setShowResults(false);
    closeSidebar(); // Fermer la sidebar après navigation
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      console.log('🔍 Touche Enter pressée, query:', searchQuery);
      
      if (searchQuery.trim().length >= 2) {
        if (searchResults.length > 0) {
          handleResultClick(searchResults[0]);
        } else {
          console.log('🔍 Aucun résultat pour cette recherche');
        }
      } else {
        console.log('🔍 Requête trop courte (minimum 2 caractères)');
      }
    } else if (e.key === 'Escape') {
      setShowResults(false);
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  // Gérer le redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      // Si on passe de mobile à desktop, on peut ajuster l'état du sidebar
      if (window.innerWidth >= 1024) {
        // Sur desktop, le sidebar peut rester ouvert
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Overlay pour fermer la sidebar sur mobile */}
      {isOpen && (
        <div 
          className="sidebar-overlay active" 
          onClick={closeSidebar}
          aria-label={t('navbar.closeMenu')}
        />
      )}
      <aside className={`admin-sidebar ${isOpen ? 'open' : 'closed'}`}>
      {/* Logo */}
      <div className="sidebar-logo">
        <BrandLogo size="sidebar" />
      </div>
      
      {/* Recherche mobile/tablette */}
      <div className="sidebar-search mobile-search" ref={searchWrapperRef}>
        <form 
          onSubmit={handleSearchSubmit}
          className="sidebar-search-wrapper"
          style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}
        >
          <MagnifyingGlassIcon className="sidebar-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('dashboardSidebar.searchPlaceholder')}
            className="sidebar-search-input"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              console.log('🔍 Input focusé');
              if (searchResults.length > 0) {
                setShowResults(true);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('🔍 Input cliqué');
            }}
            style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
          />
          <button
            type="submit"
            className="sidebar-search-button"
            onClick={(e) => {
              e.stopPropagation();
              handleSearchSubmit(e as any);
            }}
            aria-label={t('navbar.searchButton')}
            style={{
              position: 'absolute',
              right: '12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 11,
              pointerEvents: 'auto'
            }}
          >
            <MagnifyingGlassIcon 
              style={{ 
                width: '20px', 
                height: '20px', 
                color: 'var(--color-cranberry)',
                pointerEvents: 'none'
              }} 
            />
          </button>
        </form>
        
        {/* Résultats de recherche */}
        {showResults && searchResults.length > 0 && (
          <div className="sidebar-search-results">
            {searchResults.map((result, index) => (
              <div
                key={`${result.type}-${result.id}-${index}`}
                className="sidebar-search-result-item"
                onClick={() => handleResultClick(result)}
              >
                <div className="sidebar-search-result-icon">
                  {result.type === 'order' && '📦'}
                  {result.type === 'product' && '🛍️'}
                  {result.type === 'client' && '👤'}
                  {result.type === 'invoice' && '📄'}
                </div>
                <div className="sidebar-search-result-content">
                  <div className="sidebar-search-result-title">{result.title}</div>
                  {result.subtitle && (
                    <div className="sidebar-search-result-subtitle">{result.subtitle}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {showResults && searchQuery.length >= 2 && searchResults.length === 0 && (
          <div className="sidebar-search-results">
            <div className="sidebar-search-result-empty">
                {t('dashboardSidebar.noResults', { query: searchQuery })}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {/* Dashboard - Admin uniquement */}
        {isAdmin && (
          <Link 
            to="/admin/dashboard" 
            className={`nav-item ${isActive('/admin/dashboard') ? 'active' : ''}`}
            onClick={(e) => handleNavClick('/admin/dashboard', e)}
          >
            <HomeIcon className="nav-icon" />
            <span>{t('dashboardSidebar.dashboard')}</span>
          </Link>
        )}
        
        {/* Commandes - Accessible à tous */}
        <Link 
          to="/admin/commandes" 
          className={`nav-item ${isActive('/admin/commandes') ? 'active' : ''}`}
          onClick={(e) => handleNavClick('/admin/commandes', e)}
        >
          <ShoppingCartIcon className="nav-icon" />
          <span>{t('dashboardSidebar.orders')}</span>
        </Link>
        <Link
          to="/admin/commandes-professionnelles"
          className={`nav-item ${isActive('/admin/commandes-professionnelles') ? 'active' : ''}`}
          onClick={(e) => handleNavClick('/admin/commandes-professionnelles', e)}
        >
          <BuildingOffice2Icon className="nav-icon" />
          <span>{t('dashboardSidebar.professionalOrders')}</span>
        </Link>
        
        {/* Clients et CRM - Admin uniquement */}
        {isAdmin && (
          <Link 
            to="/admin/clients" 
            className={`nav-item ${isActive('/admin/clients') ? 'active' : ''}`}
            onClick={(e) => handleNavClick('/admin/clients', e)}
          >
            <UserGroupIcon className="nav-icon" />
            <span>{t('dashboardSidebar.clients')}</span>
          </Link>
        )}
        
        {/* Transaction - Admin uniquement */}
        {isAdmin && (
          <Link 
            to="/admin/transaction" 
            className={`nav-item ${isActive('/admin/transaction') ? 'active' : ''}`}
            onClick={(e) => handleNavClick('/admin/transaction', e)}
          >
            <BanknotesIcon className="nav-icon" />
            <span>{t('dashboardSidebar.transaction')}</span>
          </Link>
        )}
        
        {/* Section Facturation - Accessible à tous */}
        <div className="nav-section">
          <div className="nav-section-title">{t('dashboardSidebar.billing')}</div>
          <Link 
            to="/admin/factures" 
            className={`nav-item nav-sub-item ${isActive('/admin/factures') ? 'active' : ''}`}
            onClick={(e) => handleNavClick('/admin/factures', e)}
          >
            <DocumentTextIcon className="nav-icon" />
            <span>{t('dashboardSidebar.invoices')}</span>
          </Link>
        </div>
        
        {/* Section Analytics - Admin uniquement */}
        {isAdmin && (
          <div className="nav-section">
            <div className="nav-section-title">{t('dashboardSidebar.analytics')}</div>
            <Link 
              to="/admin/analytics/customer-behavior" 
              className={`nav-item nav-sub-item ${isActive('/admin/analytics/customer-behavior') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/analytics/customer-behavior', e)}
            >
              <PresentationChartLineIcon className="nav-icon" />
              <span>{t('dashboardSidebar.customerBehavior')}</span>
            </Link>
          </div>
        )}
        
        {/* Rapport - Admin uniquement */}
        {isAdmin && (
          <Link 
            to="/admin/rapport" 
            className={`nav-item ${isActive('/admin/rapport') ? 'active' : ''}`}
            onClick={(e) => handleNavClick('/admin/rapport', e)}
          >
            <ChartBarIcon className="nav-icon" />
            <span>{t('dashboardSidebar.report')}</span>
          </Link>
        )}

        {/* Section CMS - Admin uniquement */}
        {isAdmin && (
          <div className="nav-section">
            <div className="nav-section-title">{t('dashboardSidebar.cms')}</div>
            <Link 
              to="/admin/cms/faq" 
              className={`nav-item nav-sub-item ${isActive('/admin/cms/faq') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/cms/faq', e)}
            >
              <QuestionMarkCircleIcon className="nav-icon" />
              <span>{t('dashboardSidebar.faq')}</span>
            </Link>
            <Link 
              to="/admin/cms/testimonials" 
              className={`nav-item nav-sub-item ${isActive('/admin/cms/testimonials') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/cms/testimonials', e)}
            >
              <ChatBubbleLeftRightIcon className="nav-icon" />
              <span>{t('dashboardSidebar.testimonials')}</span>
            </Link>
            <Link
              to="/admin/cms/home-boxes"
              className={`nav-item nav-sub-item ${isActive('/admin/cms/home-boxes') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/cms/home-boxes', e)}
            >
              <RectangleGroupIcon className="nav-icon" />
              <span>Boxes Collection</span>
            </Link>
            <Link
              to="/admin/cms/home-coming-boxes"
              className={`nav-item nav-sub-item ${isActive('/admin/cms/home-coming-boxes') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/cms/home-coming-boxes', e)}
            >
              <RectangleGroupIcon className="nav-icon" />
              <span>Boxes prochainement</span>
            </Link>
          </div>
        )}
        
        {/* Section Produit - Admin uniquement */}
        {isAdmin && (
          <div className="nav-section">
            <div className="nav-section-title">{t('dashboardSidebar.product')}</div>
            <Link 
              to="/admin/ajouter-produit" 
              className={`nav-item nav-sub-item ${isActive('/admin/ajouter-produit') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/ajouter-produit', e)}
            >
              <PlusCircleIcon className="nav-icon" />
              <span>{t('dashboardSidebar.addProducts')}</span>
            </Link>
            <Link 
              to="/admin/liste-produits" 
              className={`nav-item nav-sub-item ${isActive('/admin/liste-produits') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/liste-produits', e)}
            >
              <ListBulletIcon className="nav-icon" />
              <span>{t('dashboardSidebar.productsList')}</span>
            </Link>
            <Link 
              to="/admin/code-promo" 
              className={`nav-item nav-sub-item ${isActive('/admin/code-promo') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/code-promo', e)}
            >
              <TicketIcon className="nav-icon" />
              <span>{t('dashboardSidebar.promoCode')}</span>
            </Link>
            <Link 
              to="/admin/categories" 
              className={`nav-item nav-sub-item ${isActive('/admin/categories') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/categories', e)}
            >
              <Squares2X2Icon className="nav-icon" />
              <span>{t('dashboardSidebar.categories')}</span>
            </Link>
            <Link 
              to="/admin/avis-produits" 
              className={`nav-item nav-sub-item ${isActive('/admin/avis-produits') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/avis-produits', e)}
            >
              <StarIcon className="nav-icon" />
              <span>{t('dashboardSidebar.reviews')}</span>
            </Link>
            <Link 
              to="/admin/gestion-stock" 
              className={`nav-item nav-sub-item ${isActive('/admin/gestion-stock') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/gestion-stock', e)}
            >
              <CubeIcon className="nav-icon" />
              <span>{t('dashboardSidebar.stock')}</span>
            </Link>
          </div>
        )}

        {/* Section Administration - Admin uniquement sauf Paramètres */}
        <div className="nav-section">
          <div className="nav-section-title">{t('dashboardSidebar.administration')}</div>
          {isAdmin && (
            <Link 
              to="/admin/utilisateurs" 
              className={`nav-item nav-sub-item ${isActive('/admin/utilisateurs') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/utilisateurs', e)}
            >
              <UserCircleIcon className="nav-icon" />
              <span>{t('dashboardSidebar.users')}</span>
            </Link>
          )}
          {/* Paramètres - Accessible à tous */}
          <Link 
            to="/admin/parametres" 
            className={`nav-item nav-sub-item ${isActive('/admin/parametres') ? 'active' : ''}`}
            onClick={(e) => handleNavClick('/admin/parametres', e)}
          >
            <Cog6ToothIcon className="nav-icon" />
            <span>{t('dashboardSidebar.settings')}</span>
          </Link>
          {isAdmin && (
            <Link 
              to="/admin/autorite-controle" 
              className={`nav-item nav-sub-item ${isActive('/admin/autorite-controle') ? 'active' : ''}`}
              onClick={(e) => handleNavClick('/admin/autorite-controle', e)}
            >
              <ShieldCheckIcon className="nav-icon" />
              <span>{t('dashboardSidebar.authority')}</span>
            </Link>
          )}
        </div>
      </nav>

      {/* Footer utilisateur */}
      <div className="sidebar-footer">
        <div className="admin-user-card">
          <div className="admin-avatar">
            {/* Utiliser directement le placeholder au lieu de charger une image qui n'existe pas */}
            <div className="avatar-placeholder" style={{ display: 'flex' }}>
              {user?.fullName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'A'}
            </div>
          </div>
          <div className="admin-info">
            <div className="admin-name">{user?.fullName || 'Admin'}</div>
            <div className="admin-email">{user?.email || 'a.yagoub@email.com'}</div>
          </div>
          <button onClick={handleLogout} className="admin-exit-icon">
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
          </button>
        </div>
        <Link
          to="/"
          className="boutique-card"
          onClick={() => {
            const isMobile = window.innerWidth < 1024;
            if (isMobile) {
              closeSidebar();
            }
          }}
        >
          <ShoppingBagIcon className="boutique-icon" />
          <span>{t('dashboardSidebar.shop')}</span>
          <ArrowTopRightOnSquareIcon className="external-link-icon" />
        </Link>
      </div>
    </aside>
    </>
  );
};

